/**
 * Store links + launch phase — the single source of truth for every
 * App Store / Google Play CTA on the site (SPA and the serverless OG
 * cards import from here). Pure module: no window/navigator/document,
 * so api/_lib can import it in the Node runtime.
 *
 * NEVER SHIP A DEAD LINK. Both store pages return 404 until they are
 * published (the Apple pre-order page publishes only after App Review;
 * the Play page after pre-registration is switched on). So the CTAs are
 * driven by LAUNCH_PHASE, and while it is 'prelaunch' they render as
 * non-interactive "coming soon" markers rather than links to a 404.
 *
 * To flip phases, follow DEPLOY.md § "Launch flip runbook" (the single
 * authoritative go-live checklist).
 */

/** From App Store Connect → My Apps → Muse → App Information → Apple ID. */
export const APP_STORE_ID: string | null = '6757545481';

/** Android application id (com.muse.photography). */
export const PLAY_APP_ID = 'com.muse.photography';

/** Resolves listings only by numeric id; null hides every Apple CTA. */
export const APP_STORE_URL: string | null = APP_STORE_ID
  ? `https://apps.apple.com/app/id${APP_STORE_ID}`
  : null;

/** Canonical Play URL + a web Install Referrer for attribution. */
export const PLAY_STORE_URL: string | null = `https://play.google.com/store/apps/details?id=${PLAY_APP_ID}&referrer=utm_source%3Dmusephoto.app%26utm_medium%3Dweb%26utm_campaign%3Dprelaunch`;

export const SITE_URL = 'https://musephoto.app';

export type LaunchPhase = 'prelaunch' | 'preorder' | 'live';

/**
 * The one switch. 'prelaunch' → CTAs are non-interactive "coming soon"
 * markers. 'preorder' → live pre-order / pre-registration links.
 * 'live' → download / get-it links. See DEPLOY.md § "Launch flip runbook".
 */
export const LAUNCH_PHASE: LaunchPhase = 'prelaunch'; // Flip per DEPLOY.md § Launch flip runbook

/** Two-part label so the badge reads like the official artwork
    ("Pre-order on the" + "App Store") while the accessible name — the
    concatenation of both spans — still equals the full visible phrase
    (WCAG 2.5.3 Label in Name). */
export interface StoreCta {
  verb: string;
  name: string;
  /** null → this platform's page isn't live; render nothing for it. */
  href: string | null;
}

export interface StoreLabels {
  apple: StoreCta;
  play: StoreCta;
  /** Small print beneath the CTA pair. */
  note: string;
  /** false → render markers, not links (no dead links to a 404). */
  interactive: boolean;
}

export function storeLabels(phase: LaunchPhase = LAUNCH_PHASE): StoreLabels {
  switch (phase) {
    case 'live':
      return {
        apple: { verb: 'Download on the', name: 'App Store', href: APP_STORE_URL },
        play: { verb: 'Get it on', name: 'Google Play', href: PLAY_STORE_URL },
        note: 'Free on iOS & Android',
        interactive: true,
      };
    case 'preorder':
      return {
        // Apple's official pre-launch badge is "Pre-order on the App Store"
        // (Apple has no "pre-register" badge); Google's is "Pre-register on
        // Google Play". The text links mirror the official badge wording.
        apple: { verb: 'Pre-order on the', name: 'App Store', href: APP_STORE_URL },
        play: { verb: 'Pre-register on', name: 'Google Play', href: PLAY_STORE_URL },
        note: 'Free at launch · iOS & Android',
        interactive: true,
      };
    case 'prelaunch':
    default:
      return {
        apple: { verb: 'Coming soon —', name: 'App Store', href: APP_STORE_URL },
        play: { verb: 'Coming soon —', name: 'Google Play', href: PLAY_STORE_URL },
        note: 'Coming soon to iOS & Android · Free at launch',
        interactive: false,
      };
  }
}

/** Availability sentence for inline copy (step 01, Guide, hero). */
export function availabilityLine(phase: LaunchPhase = LAUNCH_PHASE): string {
  switch (phase) {
    case 'live':
      return 'Muse is free on the App Store and Google Play.';
    case 'preorder':
      return 'Pre-order Muse on the App Store or pre-register on Google Play, and it installs itself on launch day.';
    case 'prelaunch':
    default:
      return 'Muse is coming to the App Store and Google Play — free at launch.';
  }
}
